// ---------------------------------------------------------------
// Application bootstrap. Order is intentional:
//   1. SCSS first — palette CSS vars exist before PrimeVue resolves
//      its design tokens.
//   2. Pinia before Router — stores are available inside guards.
//   3. Router before PrimeVue services — services pull config from
//      the active route at install time on some integrations.
//   4. ConfirmationService + DialogService — backing services for
//      PrimeVue's `useConfirm()` / `useDialog()` composables. The
//      host components (`<ConfirmDialog />`, `<DynamicDialog />`)
//      mount in `App.vue`.
//   5. globalPromiseHandler — prod-only suppression of noisy
//      unhandled rejections.
// ---------------------------------------------------------------

import '@/assets/styles/main.scss'

import { createApp } from 'vue'
import { vNoAutofill } from '@/directives/noAutofill'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import ConfirmationService from 'primevue/confirmationservice'
import DialogService from 'primevue/dialogservice'
import ToastService from 'primevue/toastservice'

import App from '@/App.vue'
import router from '@/router'
import { ThemePreset } from '@/lib/theme/ThemePreset'
import globalPromiseHandler from '@/lib/plugins/globalPromiseHandler'
import { i18n } from '@/lib/i18n'
import { initObservability } from '@/lib/observability/faro'
import { emitError } from '@/lib/observability/events'
import { useGlobalStore } from '@/stores/globalStore'
import { useNotificationStore } from '@/stores/notificationStore'

// Faro SDK — initializes BEFORE `createApp` so window.onerror /
// unhandledrejection handlers are wired in time to catch boot-phase
// errors (Vue's errorHandler can't catch synchronous bootstrap throws).
initObservability()

const app = createApp(App)

/**
 * Vue-level error boundary. Renders + component-lifecycle errors
 * land here. Surfaced to the operator via a notification + the
 * console so we never silently drop a render exception, and
 * pre-wired to fan out to a real error reporter (Sentry et al.)
 * when one lands — the deferred-1 item from the audit.
 *
 * `i18n.global.t(...)` is the non-Composition translation entry:
 * `useI18n()` requires a setup scope, and this handler runs
 * outside any component.
 */
app.config.errorHandler = (err, _vm, info) => {
  console.error('[Vue errorHandler]', info, err)
  // Push the original Error to Faro so the stack trace is preserved.
  // Closure-form attrs: skipped entirely when observability latch is
  // off, building no extra context.
  emitError(err, () => ({ source: 'vue.errorHandler', info: info ?? 'unknown' }))
  try {
    const message = err instanceof Error ? err.message : i18n.global.t('app.error.unexpected')
    useNotificationStore().alertError(message, i18n.global.t('app.error.generic'))
  } catch {
    // Notification store may not be ready (very early boot
    // exceptions) — silently fall back to the console.error above
    // and the Faro push (which doesn't depend on stores).
  }
}

app
  .use(createPinia())
  .use(router)
  .use(i18n)
  .use(PrimeVue, {
    ripple: true,
    theme: {
      preset: ThemePreset,
      options: {
        // Dark mode toggled by `<html class="app-dark">`. Same selector
        // Tailwind uses (see `tailwind.config.js`) — single source of
        // truth for "is dark mode on right now."
        darkModeSelector: '.app-dark',
        cssLayer: {
          name: 'primevue',
          // Mirrors `@layer` order in `assets/styles/main.scss`.
          order: 'tailwind-base, primevue, tailwind-components, tailwind-utilities',
        },
      },
    },
    /**
     * Global passthrough — suppress browser / password-manager
     * autofill on every PV input component site-wide. Set once at
     * plugin init; zero per-instance runtime cost. Applied to the
     * actual DOM `<input>` element of each input-flavored
     * component, NOT to wrapper divs (where the attribute is
     * meaningless and password managers wouldn't see it anyway).
     *
     *   `autocomplete="off"`   — canonical opt-out.
     *   `data-form-type="other"` — hints to 1Password / LastPass /
     *                              Bitwarden that this isn't a
     *                              credential surface.
     *
     * Coverage notes:
     *   - PV's v-model implementation doesn't set a `name` attr by
     *     default, so Chrome's name-heuristic autofill (which
     *     ignores `autocomplete="off"` on `email` / `password` /
     *     `username` / `search` names) doesn't fire — we don't
     *     need to inject random names here.
     *   - `Password`, `AutoComplete`, `InputNumber` render their
     *     visible input via the nested `pcInputText` sub-component;
     *     the pt path drills into that sub-component's `root`.
     *   - `Textarea` has its `<textarea>` as the root; same shape
     *     as `InputText`.
     *   - `Select` / `DatePicker` use a button-driven control
     *     surface with no autofill exposure on the visible
     *     element; the optional search-filter input inside the
     *     panel is the `pcFilter` sub-input, which we also stamp.
     *
     * Opt back IN for the rare field that legitimately wants
     * autofill (e.g. the future profile-page email change) by
     * setting `autocomplete="email"` (or whatever) on that
     * specific input — PV inline props win over global pt.
     */
    pt: {
      inputtext: { root: { autocomplete: 'off', 'data-form-type': 'other' } },
      textarea: { root: { autocomplete: 'off', 'data-form-type': 'other' } },
      password: { pcInputText: { root: { autocomplete: 'off', 'data-form-type': 'other' } } },
      inputnumber: { pcInputText: { root: { autocomplete: 'off', 'data-form-type': 'other' } } },
      autocomplete: { pcInputText: { root: { autocomplete: 'off', 'data-form-type': 'other' } } },
      select: { pcFilter: { root: { autocomplete: 'off', 'data-form-type': 'other' } } },
      multiselect: { pcFilter: { root: { autocomplete: 'off', 'data-form-type': 'other' } } },
    },
  })
  .use(ConfirmationService)
  .use(DialogService)
  .use(ToastService)
  .use(globalPromiseHandler)

// Pinia is installed; stores can be resolved now. Initialize the
// global theme + start watching the OS preference for live updates
// (only fires when `themeMode === 'system'`).
const globalStore = useGlobalStore()
globalStore.initTheme()
globalStore.watchSystemTheme()

// `v-no-autofill` — randomized-name autofill suppression for the few fields
// where Chrome ignores the global autocomplete=off passthrough above.
app.directive('no-autofill', vNoAutofill)

app.mount('#app')
